import { WebhookSecretConfig, WebhookSecretLocation } from "@/types/workflows";

/**
 * Client-side mirror of Omega's `WebHookSecret` and `Start.CheckWebhook` rules (GAIL-993),
 * kept identical so a secret publish would reject is flagged on the canvas rather than as an
 * `InvalidNode` the author cannot act on from here. Publish-time validation runs even with the
 * webhook off, which is why `validateStartNode` checks a secret regardless of `isEnabled`.
 */

export const WEBHOOK_SECRET_PREFIX = "wh_";
export const WEBHOOK_SECRET_MIN_LENGTH = 32;

/** Omega's `Start.DefaultSecretName`. */
export const DEFAULT_WEBHOOK_SECRET_NAME = "secret";

/** `authorization` is absent on purpose — the gate strips a leading `Bearer `. */
export const RESERVED_WEBHOOK_SECRET_HEADER_NAMES = [
  "x-api-key",
  "x-org-id",
  "host",
  "cookie",
] as const;

export const RESERVED_WEBHOOK_SECRET_HEADER_PREFIXES = ["content-", "x-forwarded-"] as const;

/** A code rather than a message: the strings live in the locale files. */
export type WebhookSecretProblem =
  | "valueRequired"
  | "valuePrefix"
  | "valueTooShort"
  | "valueCharset"
  | "nameNotToken"
  | "nameReserved";

/** Radix hands `onValueChange` a bare string, and this one is written into published graph data. */
export function isWebhookSecretLocation(value: string): value is WebhookSecretLocation {
  return Object.values<string>(WebhookSecretLocation).includes(value);
}

export type ResolvedWebhookSecret = {
  value: string;
  location: WebhookSecretLocation;
  name: string;
};

/** Fills in the defaults Omega's record applies. An empty value is treated as no secret. */
export function resolveWebhookSecret(
  secret: WebhookSecretConfig | undefined
): ResolvedWebhookSecret | null {
  if (!secret?.value) return null;

  return {
    value: secret.value,
    location: secret.in ?? WebhookSecretLocation.Query,
    name: secret.name || DEFAULT_WEBHOOK_SECRET_NAME,
  };
}

/**
 * `wh_` plus 32 random bytes base64url, the shape `WebHookSecret.Generate()` produces.
 * Minted here because Omega exposes no endpoint that mints one.
 */
export function generateWebhookSecretValue(): string {
  const bytes = new Uint8Array(32);
  globalThis.crypto.getRandomValues(bytes);

  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }

  return (
    WEBHOOK_SECRET_PREFIX +
    btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replaceAll("=", "")
  );
}

function isBase64UrlChar(char: string): boolean {
  return /[A-Za-z0-9\-_]/.test(char);
}

export function validateWebhookSecretValue(value: string): WebhookSecretProblem | null {
  if (!value) return "valueRequired";
  if (!value.startsWith(WEBHOOK_SECRET_PREFIX)) return "valuePrefix";
  if (value.length < WEBHOOK_SECRET_MIN_LENGTH) return "valueTooShort";

  return [...value.slice(WEBHOOK_SECRET_PREFIX.length)].every(isBase64UrlChar)
    ? null
    : "valueCharset";
}

/** Omega's `IsHttpToken` character for character, including its omission of `%`. */
function isHttpToken(value: string): boolean {
  return value.length > 0 && /^[A-Za-z0-9!#$&'*+\-.^_`|~]+$/.test(value);
}

function isReservedHeaderName(name: string): boolean {
  const lowered = name.toLowerCase();

  return (
    RESERVED_WEBHOOK_SECRET_HEADER_NAMES.some((reserved) => reserved === lowered) ||
    RESERVED_WEBHOOK_SECRET_HEADER_PREFIXES.some((prefix) => lowered.startsWith(prefix))
  );
}

/**
 * Takes the resolved name, which is what the transformer emits: an emptied field is Omega's
 * `Name` default, not a rejection. The blocklist is header-only, as in Omega — the hook route
 * reads no query parameter of its own.
 */
export function validateWebhookSecretName(
  name: string,
  location: WebhookSecretLocation
): WebhookSecretProblem | null {
  if (!isHttpToken(name)) return "nameNotToken";

  return location === WebhookSecretLocation.Header && isReservedHeaderName(name)
    ? "nameReserved"
    : null;
}

/** The query carrier's secret is part of the URL; the header carrier's is not. */
export function hookUrlWithSecret(
  hookUrl: string,
  secret: ResolvedWebhookSecret | null
): string {
  if (!secret || secret.location !== WebhookSecretLocation.Query) return hookUrl;

  const separator = hookUrl.includes("?") ? "&" : "?";

  return `${hookUrl}${separator}${encodeURIComponent(secret.name)}=${encodeURIComponent(secret.value)}`;
}
